import { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { Download, LogOut, Search, Trash2, Users, Loader2 } from "lucide-react";
import { auth, leads, formatApiError, API_BASE } from "@/lib/api";

export default function AdminDashboard() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [redirect, setRedirect] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await leads.list();
      setItems(data);
    } catch (err) {
      if (err?.response?.status === 401) {
        auth.logout();
        setRedirect(true);
        return;
      }
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!auth.getToken()) {
      setRedirect(true);
      return;
    }
    load();
  }, [load]);

  const filtered = useMemo(() => {
    if (!query.trim()) return items;
    const q = query.trim().toLowerCase();
    return items.filter(
      (l) =>
        l.name?.toLowerCase().includes(q) ||
        l.email?.toLowerCase().includes(q) ||
        l.whatsapp?.toLowerCase().includes(q),
    );
  }, [items, query]);

  const onLogout = () => {
    auth.logout();
    setRedirect(true);
  };

  const onDelete = async (id) => {
    if (!window.confirm("Remover este lead?")) return;
    try {
      await leads.remove(id);
      setItems((prev) => prev.filter((l) => l.id !== id));
    } catch (err) {
      alert(formatApiError(err));
    }
  };

  const onExport = () => {
    const token = auth.getToken();
    // Use fetch to include auth header, then trigger download from blob
    fetch(`${API_BASE}/admin/leads/export.csv`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => {
        if (!r.ok) throw new Error("Falha ao exportar");
        return r.blob();
      })
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `leads-julssy-${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      })
      .catch((e) => alert(e.message));
  };

  if (redirect) return <Navigate to="/admin/login" replace />;

  return (
    <div className="min-h-[100svh] bg-[var(--bg)]">
      {/* Header */}
      <header className="border-b border-white/10 px-5 py-5 md:px-12">
        <div className="mx-auto flex max-w-[1440px] items-center justify-between">
          <div className="flex items-center gap-3">
            <Users className="h-4 w-4 text-[var(--pink)]" strokeWidth={2.2} />
            <span className="font-mono text-[11px] uppercase tracking-super text-[var(--pink)]">
              Julssy · Painel
            </span>
          </div>
          <div className="flex items-center gap-4">
            <span className="hidden font-mono text-[10px] uppercase tracking-super text-white/50 md:inline">
              {auth.getEmail()}
            </span>
            <button
              onClick={onLogout}
              className="inline-flex items-center gap-2 border border-white/15 px-3 py-1.5 font-mono text-[10px] uppercase tracking-super text-white/75 transition hover:border-[var(--pink)] hover:text-[var(--pink)]"
              data-testid="admin-logout"
            >
              <LogOut className="h-3.5 w-3.5" strokeWidth={2.2} />
              Sair
            </button>
          </div>
        </div>
      </header>

      {/* Main */}
      <main className="mx-auto max-w-[1440px] px-5 py-10 md:px-12">
        <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <div>
            <h1 className="font-serif text-4xl leading-[0.92] tracking-tight text-white md:text-5xl">
              Grupo <span className="italic text-[var(--pink)]">VIP.</span>
            </h1>
            <p className="mt-2 font-mono text-[12px] text-white/60">
              {items.length} cadastro{items.length === 1 ? "" : "s"} no total
            </p>
          </div>

          <div className="flex items-center gap-2">
            <div className="relative">
              <Search
                className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/40"
                strokeWidth={2.2}
              />
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Buscar nome, email, whatsapp"
                className="input pl-9"
                data-testid="admin-search"
              />
            </div>
            <button
              onClick={onExport}
              className="btn-ghost"
              disabled={items.length === 0}
              data-testid="admin-export"
            >
              <Download className="h-3.5 w-3.5" strokeWidth={2.2} />
              CSV
            </button>
          </div>
        </div>

        {/* Table */}
        <div
          className="mt-8 overflow-x-auto border border-white/10"
          data-testid="admin-leads-table"
        >
          <table className="w-full min-w-[720px] border-collapse">
            <thead>
              <tr className="border-b border-white/10 bg-white/[0.02]">
                <Th>Nome</Th>
                <Th>Email</Th>
                <Th>WhatsApp</Th>
                <Th>Aceite</Th>
                <Th>Cadastro</Th>
                <Th className="text-right">Ações</Th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={6} className="p-10 text-center">
                    <Loader2 className="mx-auto h-5 w-5 animate-spin text-white/40" />
                  </td>
                </tr>
              )}
              {!loading && error && (
                <tr>
                  <td colSpan={6} className="p-10 text-center font-mono text-xs text-red-300">
                    {error}
                  </td>
                </tr>
              )}
              {!loading && !error && filtered.length === 0 && (
                <tr>
                  <td
                    colSpan={6}
                    className="p-16 text-center font-mono text-xs uppercase tracking-super text-white/40"
                  >
                    {items.length === 0
                      ? "Nenhum lead cadastrado ainda."
                      : "Nenhum resultado para essa busca."}
                  </td>
                </tr>
              )}
              {!loading &&
                !error &&
                filtered.map((l) => (
                  <tr
                    key={l.id}
                    className="border-b border-white/5 transition hover:bg-white/[0.02]"
                    data-testid={`admin-lead-row-${l.id}`}
                  >
                    <Td className="font-medium text-white">{l.name}</Td>
                    <Td className="text-white/80">{l.email}</Td>
                    <Td className="text-white/80">{l.whatsapp}</Td>
                    <Td>
                      <span className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-super text-[var(--pink)]">
                        <span className="h-1.5 w-1.5 rounded-full bg-[var(--pink)]" />
                        {l.consent ? "sim" : "não"}
                      </span>
                    </Td>
                    <Td className="text-white/60">{formatDate(l.created_at)}</Td>
                    <Td className="text-right">
                      <button
                        onClick={() => onDelete(l.id)}
                        className="inline-flex items-center gap-1 border border-white/10 px-2 py-1 font-mono text-[10px] uppercase tracking-super text-white/60 transition hover:border-red-400/50 hover:text-red-300"
                        data-testid={`admin-lead-delete-${l.id}`}
                      >
                        <Trash2 className="h-3 w-3" strokeWidth={2.2} />
                        Remover
                      </button>
                    </Td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
}

function Th({ children, className = "" }) {
  return (
    <th
      className={`px-4 py-3 text-left font-mono text-[10px] uppercase tracking-super text-white/50 ${className}`}
    >
      {children}
    </th>
  );
}

function Td({ children, className = "" }) {
  return (
    <td className={`px-4 py-4 font-mono text-[13px] ${className}`}>{children}</td>
  );
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}
